import { getSchema } from "@tiptap/core";
import { generateHTML, generateJSON } from "@tiptap/html";
import type { Schema } from "@tiptap/pm/model";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { DocsBlocks } from "../../../Components/tiptap/extenstions/custom/DocsBlocks/DocsBlocks";
import ExtensionKit from "../../../Components/tiptap/extenstions/extension-kit";
import Communicator from "../../../Helper/Communicator";
import { handleGleapLink } from "../../../Helper/handleGleapLink";
import { helpCenterDefaultHost } from "../../../Helper/HelpCenterUrl";
import { sanitizeRichHtml } from "../../../Helper/sanitize";
import { HttpHelpCenterService } from "../../../Services/http.clients/http.helpcenter.client";
import { useConfigStore } from "../../../Stores/Config";
import { useHelpCenterStore } from "../../../Stores/HelpCenter";
import { useRouterStore } from "../../../Stores/Router";
import ArticleRating from "./ArticleRating";
import { Loading, NotFound } from "../HelpCenterShared/HelpCenterShared";
import "./HelpCenterArticle.scss";

// A node or mark the widget doesn't know (written by a newer editor) makes
// generateHTML throw and blanks the whole article. Keep its content instead:
// unknown nodes are replaced by their children, unknown marks are dropped.
function degradeUnknownNodes(node: any, schema: Schema): any[] {
  if (!node || typeof node !== "object") return [];

  const content = Array.isArray(node.content)
    ? node.content.flatMap((child: any) => degradeUnknownNodes(child, schema))
    : undefined;

  if (!schema.nodes[node.type]) return content ?? [];

  const result = { ...node };
  if (content) result.content = content;
  if (Array.isArray(node.marks)) {
    result.marks = node.marks.filter((mark: any) => schema.marks[mark?.type]);
  }
  return [result];
}

function getArticleHTML(content: any): string {
  try {
    if (!content) return "";

    const extensions = [...ExtensionKit(), ...DocsBlocks];

    // `content` is normally a TipTap/ProseMirror JSON document. Some articles
    // (AI-generated or imported) store the body as an HTML string instead, and
    // generateHTML() only accepts ProseMirror JSON — passing a string makes it
    // throw, which blanked the whole body while the title/description (plain
    // text) still rendered. Normalize a string body into JSON first. This is
    // safe: generateJSON parses the HTML through the same TipTap schema, so
    // anything not in the schema (<script>, inline event handlers, unknown
    // tags/attrs) is dropped — the output still flows through the exact same
    // sanitized generateHTML path as normal JSON articles. Mirrors the fix
    // already shipped on the hosted help center (HelpCenterApp).
    let doc = content;
    if (typeof content === "string") {
      const trimmed = content.trim();
      if (!trimmed) return "";
      if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
        // Body stored as a stringified ProseMirror document.
        try {
          doc = JSON.parse(trimmed);
        } catch {
          doc = generateJSON(content, extensions);
        }
      } else {
        // Body stored as an HTML string.
        doc = generateJSON(content, extensions);
      }
    }

    const schema = getSchema(extensions);
    const [safeDoc] = degradeUnknownNodes(doc, schema);
    if (!safeDoc) return "";

    // The schema keeps node attributes as written (an iframe's `src`, link
    // hrefs), so the HTML still goes through the sanitizer.
    return sanitizeRichHtml(generateHTML(safeDoc, extensions));
  } catch {
    return "";
  }
}

export function ArticleDetailView({ articleId }: { articleId: string }) {
  const currentArticle = useHelpCenterStore((s) => s.currentArticle);
  const loadArticle = useHelpCenterStore((s) => s.loadArticle);
  const helpCenterConfig = useHelpCenterStore((s) => s.getHelpCenterConfig());
  const configStore = useConfigStore();
  const contentRef = useRef<HTMLDivElement>(null);

  const alreadyLoaded = (() => {
    if (!currentArticle) return false;
    const article = currentArticle.article ?? currentArticle;
    return (article?.docId ?? article?.id) === articleId;
  })();

  const [loading, setLoading] = useState(!alreadyLoaded);
  // Id of the article whose load failed (404 for a deleted article or a help
  // center the plan no longer includes). Keyed by id so a late answer for a
  // previous article never marks the current one.
  const [failedArticleId, setFailedArticleId] = useState<string | null>(null);

  useEffect(() => {
    Communicator.pageChanged("appextended");
    HttpHelpCenterService.getInstance().trackArticleView(articleId);
    if (alreadyLoaded) return;
    setLoading(true);
    setFailedArticleId((failed) => (failed === articleId ? null : failed));
    loadArticle(articleId)
      .then((data) => {
        if (!data?.data) setFailedArticleId(articleId);
      })
      .finally(() => setLoading(false));
  }, [articleId, loadArticle, alreadyLoaded]);

  useEffect(() => {
    const container = contentRef.current;
    if (!container) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // Tabs blocks: the first tab shows without JS, a click switches.
      const tabButton = target.closest<HTMLElement>(".docs-tabs-button");
      if (tabButton) {
        e.preventDefault();
        const tabs = tabButton.closest(".docs-tabs");
        const index = Number(tabButton.dataset.index);
        tabs?.classList.add("is-switched");
        tabs
          ?.querySelectorAll(":scope > .docs-tabs-bar > .docs-tabs-button")
          .forEach((button, i) => {
            button.classList.toggle("is-active", i === index);
            button.setAttribute("aria-selected", i === index ? "true" : "false");
          });
        tabs
          ?.querySelectorAll(":scope > .docs-tabs-panels > .docs-tab")
          .forEach((panel, i) => panel.classList.toggle("is-active", i === index));
        return;
      }

      const anchor = target.closest("a");
      if (!anchor) return;

      // 1. gleap:// protocol (internal routing)
      if (anchor.href.startsWith("gleap://")) {
        e.preventDefault();
        e.stopPropagation();

        // Delegate to the shared handler so every link type is routed
        // (checklist, tour, survey, news, bot, action, ...) and not just
        // articles and collections.
        handleGleapLink(anchor.href, false);
        return;
      }

      // 2. HelpcenterLinkExtension elements — use data-id directly
      if (anchor.classList.contains("helpcenter-article-link")) {
        const id = anchor.getAttribute("data-id");
        if (id) {
          e.preventDefault();
          e.stopPropagation();
          useHelpCenterStore.getState().openArticle(id);
          return;
        }
      }

      // 3. Help center URL detection — match domain or relative path
      try {
        const hrefAttr = anchor.getAttribute("href") || "";
        const isRelative = hrefAttr.startsWith("/");
        const url = new URL(anchor.href);
        const { config } = useConfigStore.getState();
        const defaultDomain = config.defaultDomainHelpCenter
          ? helpCenterDefaultHost(config)
          : null;
        const customDomain = config.customDomainHelpCenter || null;

        const isHelpCenterUrl =
          isRelative ||
          (defaultDomain && url.hostname === defaultDomain) ||
          (customDomain && url.hostname === customDomain);

        if (isHelpCenterUrl) {
          const segments = url.pathname.split("/").filter(Boolean);
          const artIdx = segments.indexOf("articles");
          const colIdx = segments.indexOf("collections");

          if (artIdx !== -1 && artIdx + 1 < segments.length) {
            e.preventDefault();
            e.stopPropagation();
            const docId = segments[artIdx + 1].split("-")[0];
            if (docId) {
              useHelpCenterStore.getState().openArticle(docId);
              return;
            }
          }
          if (colIdx !== -1 && colIdx + 1 < segments.length) {
            e.preventDefault();
            e.stopPropagation();
            const docId = segments[colIdx + 1].split("-")[0];
            if (docId) {
              useRouterStore
                .getState()
                .pushPage("helpcenter-collection", { id: docId });
              return;
            }
          }
        }
      } catch {}

      // 4. Default: open externally
      if (
        anchor.getAttribute("target") === "_blank" ||
        !anchor.href.startsWith(window.location.origin)
      ) {
        e.preventDefault();
        Communicator.openURL(anchor.href, true, { placeholders: false });
      }
    };

    container.addEventListener("click", handleClick, true);
    return () => container.removeEventListener("click", handleClick, true);
  }, [currentArticle, loading]);

  if (loading) return <Loading />;

  // Not loaded: show the empty state, never a spinner that stays forever (and
  // never the previously opened article under this article's id).
  const article = currentArticle?.article ?? currentArticle;
  if (failedArticleId === articleId || !article) return <NotFound />;

  const htmlContent = getArticleHTML(article.content);
  const author = article.author;
  const lang = configStore.getCurrentLanguage?.() || "en";

  return (
    <div className="helpcenter-article-detail">
      <div
        className="helpcenter-article-detail-title"
        role="heading"
        aria-level={1}
      >
        {article.title}
      </div>
      {article.description && (
        <div className="helpcenter-article-detail-description">
          {article.description}
        </div>
      )}
      {helpCenterConfig?.showAuthor &&
        author &&
        (author.firstName || author.lastName) && (
          <div className="helpcenter-article-detail-author">
            {author.profileImageUrl && (
              <img
                className="helpcenter-article-detail-author-image"
                src={author.profileImageUrl}
                alt=""
              />
            )}
            <div className="helpcenter-article-detail-author-details">
              <div className="helpcenter-article-detail-author-name">
                {author.firstName} {author.lastName}
              </div>
              {article.updatedAt && (
                <div className="helpcenter-article-detail-author-date">
                  {moment(article.updatedAt).locale(lang).format("LL")}
                </div>
              )}
            </div>
          </div>
        )}
      {htmlContent && (
        <div
          ref={contentRef}
          className="helpcenter-article-detail-content ProseMirror"
          dangerouslySetInnerHTML={{ __html: htmlContent }}
        />
      )}
      {(helpCenterConfig?.showSurveyArticle ?? true) && (
        <ArticleRating
          articleId={articleId}
          lang={lang}
          helpcenterConfig={helpCenterConfig}
        />
      )}
    </div>
  );
}
